import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ImageIcon } from 'lucide-react';
import { PAGE_WIDTH, PAGE_HEIGHT, cornerRadiiToCss } from '@/types/yearbook';
import { LayoutTemplateDef, SlotDef, TextTemplateDef, ExtraTemplateDef } from '@/lib/parseTemplateObjects';

interface TemplatePreviewProps {
  def: LayoutTemplateDef;
  pictures?: { url: string }[];
  showTextContent?: boolean;
  className?: string;
}

type PreviewItem =
  | { kind: 'slot'; data: SlotDef; idx: number }
  | { kind: 'extra'; data: ExtraTemplateDef; idx: number }
  | { kind: 'text'; data: TextTemplateDef; idx: number };

const zOrder = (z?: 'front' | 'back', zi?: number) => (zi != null ? zi : z === 'back' ? -1 : z === 'front' ? 1 : 0);
const zIndexCss = (z?: 'front' | 'back', zi?: number) => (zi != null ? zi : z === 'front' ? 10 : z === 'back' ? 0 : 1);

const TemplatePreview: React.FC<TemplatePreviewProps> = ({ def, pictures, showTextContent = false, className }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [previewScale, setPreviewScale] = useState(1);

  const computeScale = useCallback(() => {
    if (containerRef.current) {
      setPreviewScale(containerRef.current.clientWidth / PAGE_WIDTH);
    }
  }, []);

  useEffect(() => {
    computeScale();
    const obs = new ResizeObserver(computeScale);
    if (containerRef.current) obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, [computeScale]);

  // Merge all items into a single list sorted by zPriority
  const items: PreviewItem[] = [
    ...def.slots.map((s, i) => ({ kind: 'slot' as const, data: s, idx: i })),
    ...def.extras.map((e, i) => ({ kind: 'extra' as const, data: e, idx: i })),
    ...def.texts.map((t, i) => ({ kind: 'text' as const, data: t, idx: i })),
  ];

  items.sort((a, b) => {
    const zA = a.data.zPriority;
    const zB = b.data.zPriority;
    const ziA = (a.data as any).zIndex;
    const ziB = (b.data as any).zIndex;
    return zOrder(zA, ziA) - zOrder(zB, ziB);
  });

  let picIdx = 0;

  return (
    <div
      ref={containerRef}
      className={`w-full relative overflow-hidden ${className || ''}`}
      style={{
        aspectRatio: `${PAGE_WIDTH} / ${PAGE_HEIGHT}`,
        backgroundColor: def.backgroundColor || '#ffffff',
        borderRadius: '0.125rem',
        backgroundImage: def.backgroundImageUrl ? `url(${def.backgroundImageUrl})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {items.map((item) => {
        if (item.kind === 'slot') {
          const slot = item.data;
          const hasPreview = !!slot.previewImageUrl;
          const injectedPic = !hasPreview && pictures && pictures.length > 0
            ? pictures[picIdx++ % pictures.length]
            : null;
          const imgUrl = slot.previewImageUrl || injectedPic?.url;

          const hasTemplateImage = !!slot.previewImageUrl;
          return (
            <div
              key={`s${item.idx}`}
              className={`absolute overflow-hidden flex items-center justify-center ${hasTemplateImage ? '' : 'bg-editor-sidebar-border'}`}
              style={{
                left: `${(slot.x / PAGE_WIDTH) * 100}%`,
                top: `${(slot.y / PAGE_HEIGHT) * 100}%`,
                width: `${(slot.width / PAGE_WIDTH) * 100}%`,
                height: `${(slot.height / PAGE_HEIGHT) * 100}%`,
                borderRadius: hasTemplateImage ? undefined : (cornerRadiiToCss(slot.cornerRadii, previewScale) || (slot.isCircle ? '50%' : '0.125rem')),
                backgroundColor: hasTemplateImage ? undefined : (slot.backgroundColor || undefined),
                zIndex: zIndexCss(slot.zPriority, slot.zIndex),
                transform: slot.rotation ? `rotate(${slot.rotation}deg)` : undefined,
              }}
            >
              {imgUrl ? (
                <img src={imgUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
              ) : (
                <ImageIcon
                  style={{ width: `${Math.min(slot.width, slot.height) * 0.3 / PAGE_WIDTH * 100}%`, height: 'auto', opacity: 0.4 }}
                  className="text-muted-foreground"
                  strokeWidth={1.5}
                />
              )}
            </div>
          );
        }

        if (item.kind === 'extra') {
          const ext = item.data;
          const posStyle: React.CSSProperties = {
            left: `${(ext.x / PAGE_WIDTH) * 100}%`,
            top: `${(ext.y / PAGE_HEIGHT) * 100}%`,
            width: `${(ext.width / PAGE_WIDTH) * 100}%`,
            height: `${(ext.height / PAGE_HEIGHT) * 100}%`,
            transform: ext.rotation ? `rotate(${ext.rotation}deg)` : undefined,
            backgroundColor: ext.backgroundColor || undefined,
            zIndex: zIndexCss(ext.zPriority, ext.zIndex),
            borderRadius: cornerRadiiToCss(ext.cornerRadii, previewScale) || undefined,
          };
          if ((ext.type === 'sticker' || ext.type === 'template_sticker') && ext.imageUrl) {
            return (
              <div key={`e${item.idx}`} className="absolute overflow-hidden" style={posStyle}>
                <img src={ext.imageUrl} alt="" className="w-full h-full" style={{ objectFit: ext.forceDimension ? 'fill' : 'contain' }} loading="lazy" />
              </div>
            );
          }
          if (ext.type === 'shape') {
            return (
              <div key={`e${item.idx}`} className="absolute overflow-hidden" style={posStyle}>
                <svg width="100%" height="100%" viewBox={`0 0 ${ext.width} ${ext.height}`} preserveAspectRatio="none">
                  {ext.shapeType === 'circle' && <ellipse cx={ext.width / 2} cy={ext.height / 2} rx={ext.width / 2} ry={ext.height / 2} fill={ext.color || '#000'} />}
                  {ext.shapeType === 'line' && <line x1={0} y1={ext.height / 2} x2={ext.width} y2={ext.height / 2} stroke={ext.color || '#000'} strokeWidth={2} />}
                  {(!ext.shapeType || ext.shapeType === 'rect') && <rect width={ext.width} height={ext.height} fill={ext.color || '#000'} />}
                </svg>
              </div>
            );
          }
          if (ext.type === 'shape_border') {
            return (
              <div key={`e${item.idx}`} className="absolute" style={{ ...posStyle, border: `2px solid ${ext.color || '#000'}` }} />
            );
          }
          return null;
        }

        // text
        const txt = item.data;
        return (
          <div
            key={`t${item.idx}`}
            className="absolute overflow-hidden"
            style={{
              left: `${(txt.x / PAGE_WIDTH) * 100}%`,
              top: `${(txt.y / PAGE_HEIGHT) * 100}%`,
              width: `${(txt.width / PAGE_WIDTH) * 100}%`,
              height: `${(txt.height / PAGE_HEIGHT) * 100}%`,
              transform: txt.rotation ? `rotate(${txt.rotation}deg)` : undefined,
              backgroundColor: txt.backgroundColor || undefined,
              zIndex: zIndexCss(txt.zPriority, txt.zIndex),
            }}
          >
            {showTextContent ? (
              <span
                className="block w-full h-full leading-tight"
                style={{
                  fontSize: `${Math.max(3, txt.fontSize * (1 / (PAGE_WIDTH / 100)))}px`,
                  fontFamily: txt.fontFamily,
                  fontWeight: txt.fontWeight || '400',
                  color: txt.color,
                  textAlign: txt.textAlign || 'left',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {txt.content}
              </span>
            ) : (
              <div className="w-full h-full flex items-center justify-center border border-dashed border-muted-foreground/50 rounded-sm bg-muted-foreground/5">
                <span className="text-[4px] leading-none text-muted-foreground font-medium">T</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TemplatePreview;
